import DOMPurify from "isomorphic-dompurify";
import { store } from "../store";
import { addNotification, removeNotification } from "../store/slices/notificationSlice";

const removeClass = (className: string) => store.dispatch(removeNotification(className));

/**
 * Animate element using CSS transitions
 */
const slideDown = (element: HTMLElement, duration: number, callback?: () => void) => {
  element.style.display = "block";
  element.style.maxHeight = "0px";
  element.style.overflow = "hidden";
  element.style.transition = `max-height ${duration}ms ease-in-out, opacity ${duration}ms ease-in-out`;
  element.style.opacity = "0";

  // Trigger reflow
  element.offsetHeight;

  element.style.maxHeight = "500px";
  element.style.opacity = "1";

  if (callback) {
    setTimeout(callback, duration);
  }
};

const slideUp = (element: HTMLElement, duration: number, callback?: () => void) => {
  element.style.maxHeight = element.scrollHeight + "px";
  element.style.transition = `max-height ${duration}ms ease-in-out, opacity ${duration}ms ease-in-out`;
  element.style.overflow = "hidden";

  // Trigger reflow
  element.offsetHeight;

  element.style.maxHeight = "0px";
  element.style.opacity = "0";

  setTimeout(() => {
    element.style.display = "none";
    if (callback) {
      callback();
    }
  }, duration);
};

const hide = (element: HTMLElement) => {
  element.style.display = "none";
};

const setHtml = (element: HTMLElement, html: string) => {
  element.innerHTML = DOMPurify.sanitize(html);
};

export const notifyMessage = (
  className: string,
  time: number | null,
  message = "",
  persistent = false,
) => {
  // how this logic works
  // there can be case where user has triggered multiple notifications at once. Currently we support only only one notification (latest) at a time
  // So, what we are doing is whichever classnames we get in this method (notifyMessage), we save it in reudx state and remove when the animation time (20/30 seconds) is over
  // when we get a new call to this method, we check the redux state if there is any existing notification, if there is we remove that and save out new notification in state

  const notificationState = store.getState().notificationReducer.notificationsActive;
  notificationState.forEach((item) => {
    const element = document.getElementById(item);
    if (element) {
      hide(element);
      removeClass(item);
    }
  });

  store.dispatch(addNotification(className));

  const element = document.getElementById(className);
  if (!element) {
    return;
  }

  // there are few cases
  // time === 0, user wants to remove this message instantly, maybe it's a preexisting persistent notifcation
  if (time === 0) {
    // user wants to remove this message instantly
    slideUp(element, 0, () => removeClass(className));
  }

  // message is persistent, time is optional then, message will appear and will be shown as long as requred
  else if (persistent) {
    if (message) {
      setHtml(element, message);
    }
    slideDown(element, 3000);
  }

  // time is present and a message to be displayed is passed
  else if (message && time) {
    setHtml(element, message);
    slideDown(element, 3000, () => {
      setTimeout(() => {
        slideUp(element, 3000, () => removeClass(className));
      }, time - 6000); // time - slideDown - slideUp
    });
  }

  //time is present and there is no message passed
  else if (!message && time) {
    slideDown(element, 3000, () => {
      setTimeout(() => {
        slideUp(element, 3000, () => removeClass(className));
      }, time - 6000); // time - slideDown - slideUp
    });
  }

  //there is no time in props, similar to time === 0, message will be removed with 3 second animatin
  else if (!time) {
    slideUp(element, 3000, () => removeClass(className));
  }
};
